import { DateTimeModel, ProgressLineProps, ShipmentType, StatusProps } from "./shipmentCard.types";
import { Nullable, ValueOf } from "shared/lib/typeCheck.lib";
import { t } from "shared/localization/localization";

export type AirReferenceDTO = string;
export type AirDateTimeDTO = string;
export type AirAirlineNameDTO = string;

export const AirTransportModesDTO = {
  PLANE: "PLANE",
} as const;

export const AirTransportModesModel = {
  ...AirTransportModesDTO,
} as const;

export const AirEventCodesDTO = {
  RCT: "RCT",
  DEP: "DEP",
  ARR: "ARR",
  RCF: "RCF",
  NFD: "NFD",
} as const;

export const AirEventCodesModel = {
  ...AirEventCodesDTO,
} as const;

/** Qubictron air status_code values (main). */
export const AirMessagesDTO = {
  OK: "OK",
  WRONG_PARAMETERS: "WRONG_PARAMETERS",
  WRONG_AWB_NUMBER: "WRONG_AWB_NUMBER",
  NO_DATA: "NO_DATA",
  UNEXPECTED_ERROR: "UNEXPECTED_ERROR",
  API_KEY_LIMIT_REACHED: "API_KEY_LIMIT_REACHED",
  API_KEY_QUOTA_EXCEEDED: "API_KEY_QUOTA_EXCEEDED",
  API_KEY_RATE_LIMIT_EXCEEDED: "API_KEY_RATE_LIMIT_EXCEEDED",
  INVALID_API_KEY: "INVALID_API_KEY",
} as const;

export const AirMessagesModel = {
  ...AirMessagesDTO,
} as const;

export const airErrorMessageMap: Record<ValueOf<typeof AirMessagesModel>, string> = {
  [AirMessagesModel.OK]: "",
  [AirMessagesModel.WRONG_PARAMETERS]: t("Wrong parameters"),
  [AirMessagesModel.WRONG_AWB_NUMBER]: t("Wrong AWB number"),
  [AirMessagesModel.NO_DATA]: t("No data found for this AWB"),
  [AirMessagesModel.UNEXPECTED_ERROR]: t("Unexpected error"),
  [AirMessagesModel.API_KEY_LIMIT_REACHED]: t("API key limit reached"),
  [AirMessagesModel.API_KEY_QUOTA_EXCEEDED]: t("API key limit reached"),
  [AirMessagesModel.API_KEY_RATE_LIMIT_EXCEEDED]: t("API key rate limit exceeded"),
  [AirMessagesModel.INVALID_API_KEY]: t("Invalid API key"),
};

/** Legacy saved-card statuses (AirSavedCard still uses these). */
export const AirStatusesDTO = {
  BOOKED: "BOOKED",
  RECEIVED: "RECEIVED",
  DELIVERED: "DELIVERED",
  IN_TRANSIT: "IN_TRANSIT",
  ARRIVED: "ARRIVED",
  NOTIFIED: "NOTIFIED",
  UNKNOWN: "UNKNOWN",
  DEPARTED: "DEPARTED",
  CANCELLED: "CANCELLED",
  NULL: "NULL",
} as const;

/**
 * Search statuses (Qubictron: PLANNED / IN_TRANSIT / DELIVERED)
 * + legacy saved-list statuses for AirSavedCard / filters.
 */
export const AirStatusesModel = {
  ...AirStatusesDTO,
  PLANNED: "PLANNED",
} as const;

export const airStatusLabelMap: Record<ValueOf<typeof AirStatusesModel>, string> = {
  [AirStatusesModel.BOOKED]: t("Booked"),
  [AirStatusesModel.RECEIVED]: t("Received"),
  [AirStatusesModel.DELIVERED]: t("Delivered"),
  [AirStatusesModel.IN_TRANSIT]: t("In transit"),
  [AirStatusesModel.ARRIVED]: t("Arrived"),
  [AirStatusesModel.NOTIFIED]: t("Notified"),
  [AirStatusesModel.UNKNOWN]: t("Unknown"),
  [AirStatusesModel.DEPARTED]: t("Departed"),
  [AirStatusesModel.CANCELLED]: t("Cancelled"),
  [AirStatusesModel.NULL]: t("Unknown"),
  [AirStatusesModel.PLANNED]: t("Planned"),
};

/** @deprecated Use AirMessagesDTO — kept for older imports. */
export const AirResponseStatusesDTO = AirMessagesDTO;
export const AirResponseStatusesModel = AirMessagesModel;

export type RenderAirCardProps = {
  shipmentType: ValueOf<typeof ShipmentType>;
  isCardOpened?: boolean;
  onUpdate?: () => void;
  onDelete: () => void;
  onOpen: () => void;
  onFollow?: () => void;
  canUpdate?: boolean;
  isUpdating?: boolean;
  isDeleteProcessing?: boolean;
  reference: Nullable<string>;
  status: ValueOf<typeof AirStatusesModel> | null;
  pieces?: Nullable<string>;
  isFollowLoading?: boolean;
  className?: string;
  directionFrom: string | null;
  directionTo: string | null;
  progressLine: ProgressLineProps;
  etd: DateTimeModel | null;
  eta: DateTimeModel | null;
  ata: DateTimeModel | null;
  atd: DateTimeModel | null;
  carrierName?: string | null;
  scac?: string | null;
  tags?: string[];
};

type ErrorStatus = "not-defined" | "error" | "warn";

export type RenderAirSearchErrorCardProps = {
  className?: string;
  reference?: string | null;
  text?: string;
  status?: ErrorStatus;
  containerStatus?: StatusProps["status"];
  followLoading?: boolean;
  deleteLoading?: boolean;
  onSelect?: () => void;
  onRetry?: () => void;
  onFollow?: () => void;
  onDelete?: () => void;
};

export type AirStatusProps = StatusProps<ValueOf<typeof AirStatusesModel>>;
